/* Copyright (c) 2020-present CloudIO Inc */

import { baseFieldProps, fontStyleProps } from '../TextField/BaseField';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './IconSelect';

const IconSelectManager: ComponentManagerFn<'IconSelect'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'IconSelect'>(platform, 'IconSelect'),
    type: 'IconSelect',
    name: 'Icon Select',
    isChildAllowed: (page, item, childType) =>
      (
        ['TriggerOnBlur', 'TriggerOnChange', 'TriggerOnFocus'] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseFieldProps,
      ...fontStyleProps,
      onChangeFunction: {
        label: 'On Change',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onFocusFunction: {
        label: 'On Focus',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onBlurFunction: {
        label: 'On Blur',
        type: 'controllerFunction',
        category: 'Controllers',
      },
    },
    getDisplayText: (item) =>
      (typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value) ?? '',
    category: 'Forms',
    icon: { iconName: 'icons' },
    description: `**Overview**<br/>
The Icon Select component is a specialized form input that enables users to browse and select a visual icon from a massive library of over 7,800 SVG icons. Instead of typing an icon class name manually, users are presented with a searchable, visual picker.
Use this component in administrative interfaces or content management systems (CMS) where users need to assign visual identifiers to items—such as choosing an icon for a menu item, a service category, or a feature highlight.

**1. Data Binding**<br/>
This component binds to a text field in your database to store the identifier of the selected icon.
* **View Attribute:** Select the Data Store field to read from and write to.
* **Storage Format:** It saves the specific string name of the icon (e.g., "home", "user-circle", "arrow-right").
* **Supported Type:** **String**.

**2. Events & Logic**<br/>
The component supports standard form lifecycles, allowing you to execute logic when the user interacts with the picker.
* **Triggers:** You can attach child components like **TriggerOnChange**, **TriggerOnFocus**, and **TriggerOnBlur** to initiate actions (like auto-saving) via the low-code interface.
* **Controller Functions:** For advanced developers, specific properties allow binding directly to JavaScript/TypeScript controller functions:
    * **onChangeFunction:** Executes when a new icon is selected.
    * **onFocusFunction:** Executes when the user clicks into the field.
    * **onBlurFunction:** Executes when the user clicks away from the field.

**3. Permissions & State**<br/>
Like standard text fields, you can control the editability of the component based on user roles or logic.
* **Insertable / Updatable:** Boolean expressions that control whether the picker is active.
* **Enabled:** The user can open the picker and choose an icon.
* **Disabled (Read-Only):** The component displays the currently selected icon and its name but prevents changes. This is useful for "View Details" screens.

**4. Best Use Cases**<br/>
* **Navigation Builders:** Allowing admins to define the icon for a sidebar menu item.
* **Service Catalogs:** Letting users pick a representative symbol for a "Service Request" type (e.g., a laptop icon for IT Hardware).
* **Dashboard Configuration:** Enabling users to customize the icon displayed on their KPI cards.
* **Visual Tagging:** Assigning icons to project status tags (e.g., a "flag" for urgent items).`,
    helpRenders: [
      {
        title: 'Icon Select',
        description: 'With Default Properties',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        hideProps: true,
        visibleProps: [],
        item: {
          itemId: 'tf',
          props: {
            viewAttribute: 'name',
            flex: {
              all: 12,
            },
            label: { type: 'text', value: 'Task Icon' },
          },
          children: [],
          type: 'IconSelect',
          parent: 'form',
        },
      },
      {
        title: 'Readonly',
        description:
          'Turning off the insertable prop will disable the control while entering a new row. Updatable prop will disable editing an existing row.',
        page: SamplePage,
        exampleId: 'e6',
        height: 260,
        visibleProps: ['insertable'],
        item: {
          itemId: 'tf4',
          props: {
            label: { type: 'text', value: 'Task Icon' },
            viewAttribute: 'name',
            flex: {
              all: 12,
            },
            insertable: { type: 'boolean', value: false },
          },
          children: [],
          type: 'IconSelect',
          parent: 'form',
        },
      },
    ],
  };
};

export default IconSelectManager;
